import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons';
import { faImage } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteAccountMediaByUrl, uploadAccountMedia } from 'api/media';
import { usePanelContext } from 'components/profile-panel/PanelContext';
import * as PanelPopover from 'components/profile-panel/PanelPopover';
import { ProfileItemTriggerKeys } from 'components/profile-panel/PanelPopoverButtons';
import { usePanelPopoverContext } from 'components/profile-panel/PanelPopoverContext';
import { Upload } from 'components/upload';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

const ProfileLogo = () => {
    const { logo } = useCurrentAccount();
    const { placeholderLogo, setPlaceholderLogo } = usePanelContext();
    const [loadedPlaceholder, setLoadedPlaceholder] = useState(false);

    useEffect(() => {
        setLoadedPlaceholder(false);
    }, [placeholderLogo]);

    return (
        <PanelPopover.Root
            onOpenChange={open => {
                if (!open) {
                    setPlaceholderLogo(undefined);
                }
            }}
        >
            <PanelPopover.Trigger asChild profileItem={ProfileItemTriggerKeys.Logo}>
                <Button
                    variant="outline"
                    color="neutral"
                    className={twMerge(
                        'relative size-16 shrink-0 items-center justify-center overflow-visible bg-gray-50 disabled:pointer-events-none disabled:opacity-100 sm:size-20',
                        logo &&
                            'transition-[outline] hover:outline hover:outline-4 hover:outline-gray-200 focus:outline focus:outline-4 focus:outline-gray-200',
                    )}
                    aria-label="Update organization logo"
                >
                    <FontAwesomeIcon icon={faBuilding} className="text-gray-400" size="xl" />
                    {logo && (
                        <img
                            src={logo}
                            alt="Your organization's logo"
                            className="absolute h-full w-full rounded-md border bg-white object-contain"
                            draggable={false}
                        />
                    )}
                    {placeholderLogo && (
                        <img
                            alt=""
                            src={URL.createObjectURL(placeholderLogo)}
                            className={twMerge(
                                'absolute h-full w-full rounded-md border object-contain',
                                loadedPlaceholder && 'bg-white transition-all',
                            )}
                            draggable={false}
                            onLoad={() => {
                                setLoadedPlaceholder(true);
                            }}
                        />
                    )}
                </Button>
            </PanelPopover.Trigger>
            <PanelPopover.Content>
                <PanelPopover.Header>
                    <PanelPopover.Title icon={faImage}>Upload logo</PanelPopover.Title>
                </PanelPopover.Header>
                <Content />
            </PanelPopover.Content>
        </PanelPopover.Root>
    );
};

const Content = () => {
    const { logo, accountId } = useCurrentAccount();
    const { placeholderLogo } = usePanelContext();
    const { close } = usePanelPopoverContext();
    const { success, error } = useToast();

    const { mutate: uploadImageForLogo, isPending: isLoading } = useMutation({
        mutationFn: (file: File) => uploadAccountMedia(accountId, file, 'logos', 'default'),
        onSuccess: () => {
            success('Successfully uploaded logo');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            close();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unable to upload logo');
        },
    });

    const { mutate: deletePhoto } = useMutation({
        mutationFn: (url: string) => deleteAccountMediaByUrl(accountId, url),
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'An error occurred while clearing logo');
        },
    });

    return (
        <div className="flex flex-col gap-4 p-4">
            <LogoUpload />

            <div className="flex justify-between">
                <PanelPopover.CancelButton />
                <Button
                    variant="solid"
                    size="sm"
                    disabled={placeholderLogo === undefined || isLoading}
                    onClick={() => {
                        if (placeholderLogo) {
                            uploadImageForLogo(placeholderLogo);
                            if (logo) {
                                deletePhoto(logo);
                            }
                        }
                    }}
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

const LogoUpload = () => {
    const { logo } = useCurrentAccount();
    const { setPlaceholderLogo } = usePanelContext();
    const { error } = useToast();

    return (
        <Upload
            variant="single"
            fullWidth
            labelStyle="gap-1 md:gap-1 py-4"
            defaultFiles={logo ? [{ id: 'logo-default', url: logo }] : []}
            acceptedFileTypes={['image/*']}
            allowReorder={false}
            allowMultiple={false}
            allowReplace={true}
            allowRemove={false}
            onaddfile={(e, filePondFile) => {
                if (e) {
                    error(e?.body ?? 'Error adding logo file');
                    return;
                }

                const { file } = filePondFile;
                setPlaceholderLogo(file as File);
            }}
        />
    );
};

const ProfileLogoDisplay = () => {
    const { logo } = useCurrentAccount();
    return (
        <div
            aria-hidden
            className={twMerge(
                'relative flex size-16 shrink-0 items-center justify-center rounded-md bg-gray-50 shadow-sm ring-1 ring-inset ring-gray-300 sm:size-20',
            )}
        >
            <FontAwesomeIcon icon={faBuilding} className="text-gray-400" size="xl" />
            {logo && (
                <img
                    src={logo}
                    alt=""
                    className="absolute h-full w-full rounded-md border object-cover"
                />
            )}
        </div>
    );
};

export { ProfileLogo, ProfileLogoDisplay };
